import { useMemo } from 'react';
import { useMediaQuery } from './useMediaQuery';

/**
 * ブレークポイント定義（Ant Design準拠）
 */
export const BREAKPOINTS = {
  xs: 0,      // スマートフォン縦
  sm: 576,    // スマートフォン横
  md: 768,    // タブレット
  lg: 992,    // デスクトップ
  xl: 1200,   // 大型デスクトップ
  xxl: 1400,  // 超大型
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * レスポンシブ状態
 */
export interface ResponsiveState {
  /** 768px未満（スマートフォン） */
  isMobile: boolean;
  /** 768px以上1024px未満（タブレット） */
  isTablet: boolean;
  /** 992px以上（デスクトップ） */
  isDesktop: boolean;
  /** 576px未満（小型スマートフォン） */
  isSmallMobile: boolean;
  /** 現在のブレークポイント */
  breakpoint: Breakpoint;
  /** サイドバーを表示すべきか（992px以上） */
  showSidebar: boolean;
  /** モバイルメニュー（ドロワー）を使用すべきか（768px未満） */
  useMobileMenu: boolean;
}

/**
 * レスポンシブ状態を提供する統合フック
 *
 * @example
 * ```tsx
 * const { isMobile, showSidebar, useMobileMenu } = useResponsive();
 *
 * if (useMobileMenu) {
 *   return <Drawer>...</Drawer>;
 * }
 * return <Sider>...</Sider>;
 * ```
 */
export function useResponsive(): ResponsiveState {
  const isXs = useMediaQuery(`(max-width: ${BREAKPOINTS.sm - 1}px)`);
  const isSm = useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 1}px)`);
  const isMd = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`);
  const isLg = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px) and (max-width: ${BREAKPOINTS.xl - 1}px)`);
  const isXlOrAbove = useMediaQuery(`(min-width: ${BREAKPOINTS.xl}px)`);

  return useMemo(() => {
    // ブレークポイント判定
    let breakpoint: Breakpoint = 'xs';
    if (isXlOrAbove) breakpoint = 'xl';
    else if (isLg) breakpoint = 'lg';
    else if (isMd) breakpoint = 'md';
    else if (isSm) breakpoint = 'sm';

    const isMobile = isXs || isSm; // < 768px
    const isTablet = isMd;          // 768-991px
    const isDesktop = isLg || isXlOrAbove; // >= 992px

    return {
      isMobile,
      isTablet,
      isDesktop,
      isSmallMobile: isXs,
      breakpoint,
      showSidebar: isDesktop,           // 992px以上でサイドバー表示
      useMobileMenu: isMobile,          // 768px未満でドロワーメニュー
    };
  }, [isXs, isSm, isMd, isLg, isXlOrAbove]);
}

/**
 * レスポンシブなスタイル値を取得するユーティリティ
 */
export function getResponsiveValue<T>(
  state: ResponsiveState,
  values: { mobile?: T; tablet?: T; desktop: T }
): T {
  if (state.isMobile && values.mobile !== undefined) {
    return values.mobile;
  }
  if (state.isTablet && values.tablet !== undefined) {
    return values.tablet;
  }
  return values.desktop;
}

/**
 * レイアウト用の定数
 */
export const LAYOUT_CONSTANTS = {
  /** サイドバー展開時の幅 */
  SIDEBAR_WIDTH: 200,
  /** サイドバー折りたたみ時の幅 */
  SIDEBAR_COLLAPSED_WIDTH: 80,
  /** ヘッダーの高さ（デスクトップ） */
  HEADER_HEIGHT: 64,
  /** ヘッダーの高さ（モバイル） */
  HEADER_HEIGHT_MOBILE: 56,
  /** コンテンツのパディング（デスクトップ） */
  CONTENT_PADDING: 24,
  /** コンテンツのパディング（モバイル） */
  CONTENT_PADDING_MOBILE: 12,
  /** モバイルドロワーの幅 */
  MOBILE_DRAWER_WIDTH: 280,
} as const;
